import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import SelectOptions from "../SelectOptions";
import AutoCompleteOptions from "../AutoComplete";
import { empTypeOptions } from "../../../utils/helpers";
import { InputField } from "../Inputs";
import { Field } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import eligibilityCheckServices from "../../../services/api/service/eligibilityCheckService";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import { useDispatch } from "react-redux";
import workExperienceIcon from "./../../../assets/svgIcons/work-experience.svg";
import locationIcon from "./../../../assets/svgIcons/live-view.svg";
import {
  FirstCoApplicantEmploymentNotice,
  isFirstCoApplicant,
} from "../StepperDyanmicForm/coApplicantEmploymentLock";
import { getAutocompleteOptionValue } from "../AddNewCoApplicantModal.module";

interface Props {
  submitCount: number;
  touched: any;
  errors: any;
  employmentFieldNames: any;
  values: any;
  setFieldValue: (val: any, val2: any) => void;
  setEmpTypeError: (val: string) => void;
  empTypeError: string;
  setCompanyCodeData: (val: any) => void;
  nextCoApplicantNumber?: number;
  handleInvokPinCodeApi: (
    val1: any,
    val2: any,
    val3: any,
    val4: string,
    val5: string,
    val6: boolean,
  ) => void;
  onZipCodeLoadingChange?: (loading: boolean) => void;
}

const EmployementDetailsTab = ({
  submitCount,
  touched,
  errors,
  employmentFieldNames,
  values,
  setFieldValue,
  setEmpTypeError,
  empTypeError,
  setCompanyCodeData,
  nextCoApplicantNumber,
  handleInvokPinCodeApi,
  onZipCodeLoadingChange,
}: Props) => {
  const [zipCodeLoading, setZipCodeLoading] = useState<boolean>(false);

  // Surface this tab's own pincode-lookup loading state to the parent
  // modal so its Save button can disable while getZipCode/getPinCode are
  // still in flight.
  useEffect(() => {
    onZipCodeLoadingChange?.(zipCodeLoading);
  }, [zipCodeLoading, onZipCodeLoadingChange]);
  const [designationOptions, setDesignationOptions] = useState([]);

  const dispatch = useDispatch();
  const isFirstCoApplicantEmployment = isFirstCoApplicant(nextCoApplicantNumber);

  const companyZipCodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

 

  const getFieldError = (fieldName: string) =>
    Boolean(
      (submitCount > 0 && (errors as any)[fieldName]) ||
      ((touched as any)[fieldName] && (errors as any)[fieldName]),
    );

  const getFieldHelperText = (fieldName: string) =>
    getFieldError(fieldName) ? String((errors as any)[fieldName]) : "";

  const fetchDesignations = async () => {
    try {
      const designationResponse =
        await eligibilityCheckServices.getDesignations();
      if (designationResponse?.data?.status === "success") {
        const designationsOptions = designationResponse.data.data.map(
          (designation: any) => ({
            value: designation.genDesignation,
            label: designation.genDesgDesc,
          }),
        );
        setDesignationOptions(designationsOptions);
      }
    } catch (error) {
      console.error("Error fetching designations:", error);
    }
  };

  useEffect(() => {
    fetchDesignations();
  }, []);

  useEffect(() => {
    if (
      isFirstCoApplicantEmployment &&
      values?.[employmentFieldNames.isEmployedField] !== "Yes"
    ) {
      setFieldValue(employmentFieldNames.isEmployedField, "Yes");
    }
  }, [
    employmentFieldNames.isEmployedField,
    isFirstCoApplicantEmployment,
    setFieldValue,
    values,
  ]);

  const debouncedCompanyZipCodeCall = useCallback((pincode: string) => {
    // Clear existing timeout
    if (companyZipCodeTimeoutRef.current) {
      clearTimeout(companyZipCodeTimeoutRef.current);
    }

    // Clear loading state when user starts typing again
    setZipCodeLoading(false);

    // Set new timeout
    companyZipCodeTimeoutRef.current = setTimeout(async () => {
      if (pincode && pincode.length === 6) {
        setZipCodeLoading(true);

        try {
          const resp = await eligibilityCheckServices.getZipCode(pincode);
          if (resp.status === 200) {
            if (resp?.data?.data.length > 0) {
              const { cityName, countryName, stateName, stateCode }: any =
                resp?.data?.data?.[0];

              await handleInvokPinCodeApi(
                pincode,
                "employment",
                stateCode,
                pincode,
                "",
                false,
              );
              setFieldValue(employmentFieldNames.city, cityName || "");
              setFieldValue(employmentFieldNames.state, stateName || "");
              setFieldValue(
                employmentFieldNames.companyCountry,
                countryName || "",
              );
            } else {
              // Clear company code data if pincode not found
              setCompanyCodeData(null);
              setFieldValue(employmentFieldNames.city, "");
              setFieldValue(employmentFieldNames.state, "");
              setFieldValue(employmentFieldNames.companyCountry, "");

              dispatch(
                setStatusPopup({
                  status: "error",
                  message: "Pincode not found. Please enter a valid pincode.",
                }),
              );
            }
          }
        } catch (error: any) {
          console.log("error", error);
        } finally {
          setZipCodeLoading(false);
        }
      }
    }, 500); // 500ms delay
  }, []);

  const formatNumberIndian = (value: string | number) => {
    if (value) {
      const num = Number(value?.toString().replace(/,/g, ""));
      if (isNaN(num)) return value?.toString();

      return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(num);
    }
  };

  const handleAmountFieldChange = (fieldName: string, rawValue: string) => {
    const sanitizedValue = rawValue.replace(/[^0-9,]/g, "");
    const formattedValue = formatNumberIndian(sanitizedValue) || "";
    setFieldValue(fieldName, formattedValue);
  };

  const handleNumericFieldChange = (
    fieldName: string,
    rawValue: string,
    maxLength: number,
  ) => {
    const sanitizedValue = rawValue.replace(/\D/g, "").slice(0, maxLength);
    setFieldValue(fieldName, sanitizedValue);
  };

  const selectedEmploymentType = getAutocompleteOptionValue(
    values?.[employmentFieldNames.employmentTypeField],
  );
  const isFarmerOrOthersEmploymentType = ["farmer", "others", "other"].includes(
    selectedEmploymentType.toLowerCase(),
  );
  const showFullEmploymentFields =
    Boolean(selectedEmploymentType) && !isFarmerOrOthersEmploymentType;

  return (
    <Box sx={{ px: { xs: 0, sm: 1 }, pb: 1 }}>
      {isFirstCoApplicantEmployment && <FirstCoApplicantEmploymentNotice />}
      <FormControl
        component="fieldset"
        error={
          (submitCount > 0 &&
            !!(errors as any)[employmentFieldNames.isEmployedField]) ||
          ((touched as any)[employmentFieldNames.isEmployedField] &&
            !!(errors as any)[employmentFieldNames.isEmployedField])
        }
        sx={{
          display: "inline",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <label className="text-lable" style={{ marginRight: "32px" }}>
          Are you currently Employed? <span style={{ color: "red" }}>*</span>
        </label>
        <RadioGroup
          row
          value={
            isFirstCoApplicantEmployment
              ? "Yes"
              : values[employmentFieldNames.isEmployedField]
          }
          onChange={(e: any) => {
            if (isFirstCoApplicantEmployment) return;
            if (e.target.value === "No") {
              setFieldValue(employmentFieldNames.employmentTypeField, "");
            }
            setFieldValue(employmentFieldNames.isEmployedField, e.target.value);
          }}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio disabled={isFirstCoApplicantEmployment} />}
            disabled={isFirstCoApplicantEmployment}
            label={<span className="text-lable">Yes</span>}
          />
          <FormControlLabel
            value="No"
            control={<Radio disabled={isFirstCoApplicantEmployment} />}
            disabled={isFirstCoApplicantEmployment}
            label={<span className="text-lable">No</span>}
          />
        </RadioGroup>
        <FormHelperText>
          {(submitCount > 0 ||
            (touched as any)[employmentFieldNames.isEmployedField]) &&
          (errors as any)[employmentFieldNames.isEmployedField]
            ? String((errors as any)[employmentFieldNames.isEmployedField])
            : ""}
        </FormHelperText>
      </FormControl>
      {values[employmentFieldNames.isEmployedField] === "Yes" && (
        <>
          <Grid container spacing={2} mb={1}>
            <Grid item xs={12} sm={6}>
              <Box>
                <label className="text-lable" style={{ display: "block" }}>
                  Employment Type <span style={{ color: "red" }}>*</span>
                </label>
                <SelectOptions
                  label=""
                  value={
                    (values as any)[
                      employmentFieldNames.employmentTypeField
                    ] as string | number | undefined
                  }
                  options={empTypeOptions}
                  isMandatory={false}
                  placeholder="Select Employment Type"
                  onChange={(val) => {
                    setFieldValue(
                      employmentFieldNames.employmentTypeField,
                      val,
                    );
                    setEmpTypeError("");
                  }}
                  error={
                    (submitCount > 0 &&
                      !!(errors as any)[
                        employmentFieldNames.employmentTypeField
                      ]) ||
                    (!!(touched as any)[
                      employmentFieldNames.employmentTypeField
                    ] &&
                      !!(errors as any)[
                        employmentFieldNames.employmentTypeField
                      ])
                  }
                  helperText={
                    (submitCount > 0 ||
                      (touched as any)[
                        employmentFieldNames.employmentTypeField
                      ]) &&
                    (errors as any)[employmentFieldNames.employmentTypeField]
                      ? String(
                          (errors as any)[
                            employmentFieldNames.employmentTypeField
                          ],
                        )
                      : ""
                  }
                />
              </Box>
            </Grid>
          </Grid>
          {selectedEmploymentType && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box>
                <label className="text-lable" style={{ display: "block" }}>
                  Monthly Financial Commitments{" "}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <Field name={employmentFieldNames.monthlyObligation}>
                  {({ field: f }: any) => (
                    <InputField
                      {...f}
                      fullWidth
                      placeholder="Monthly Financial Commitments"
                      variant="outlined"
                      inputProps={{
                        maxLength: 20,
                      }}
                      value={
                        (values as any)[
                          employmentFieldNames.monthlyObligation
                        ] || ""
                      }
                      onChange={(e: any) => {
                        handleAmountFieldChange(
                          employmentFieldNames.monthlyObligation,
                          e.target.value,
                        );
                      }}
                      error={
                        (submitCount > 0 &&
                          !!(errors as any)[
                            employmentFieldNames.monthlyObligation
                          ]) ||
                        (!!(touched as any)[
                          employmentFieldNames.monthlyObligation
                        ] &&
                          !!(errors as any)[
                            employmentFieldNames.monthlyObligation
                          ])
                      }
                      helperText={
                        (submitCount > 0 ||
                          (touched as any)[
                            employmentFieldNames.monthlyObligation
                          ]) &&
                        (errors as any)[employmentFieldNames.monthlyObligation]
                          ? String(
                              (errors as any)[
                                employmentFieldNames.monthlyObligation
                              ],
                            )
                          : ""
                      }
                    />
                  )}
                </Field>
              </Box>
            </Grid>
            {showFullEmploymentFields && (
                <Grid item xs={12} sm={6}>
                  <Box>
                    <label className="text-lable" style={{ display: "block" }}>
                      Company Name <span style={{ color: "red" }}>*</span>
                    </label>
                    <Field name={employmentFieldNames.companyName}>
                      {({ field: f }: any) => (
                        <InputField
                          {...f}
                          fullWidth
                          placeholder="Enter Company Name"
                          variant="outlined"
                          value={
                            (values as any)[employmentFieldNames.companyName] ||
                            ""
                          }
                          onChange={(e: any) =>
                            setFieldValue(
                              employmentFieldNames.companyName,
                              e.target.value,
                            )
                          }
                          error={
                            (submitCount > 0 &&
                              !!(errors as any)[
                                employmentFieldNames.companyName
                              ]) ||
                            (!!(touched as any)[
                              employmentFieldNames.companyName
                            ] &&
                              !!(errors as any)[
                                employmentFieldNames.companyName
                              ])
                          }
                          helperText={
                            (submitCount > 0 ||
                              (touched as any)[
                                employmentFieldNames.companyName
                              ]) &&
                            (errors as any)[employmentFieldNames.companyName]
                              ? String(
                                  (errors as any)[
                                    employmentFieldNames.companyName
                                  ],
                                )
                              : ""
                          }
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>
              )}
            {showFullEmploymentFields && (
                <Grid item xs={12} sm={6}>
                  <Box>
                    <AutoCompleteOptions
                      label="Designation"
                      options={designationOptions}
                      value={
                        designationOptions.find(
                          (opt: any) =>
                            String(opt.value) ===
                            String(
                              (values as any)[employmentFieldNames.designation] ?? "",
                            ),
                        ) || null
                      }
                      placeholder="Select Designation"
                      onChange={(opt: any) => {
                        const nextValue = opt ? opt.value : "";
                        setFieldValue(employmentFieldNames.designation, nextValue);
                      }}
                      error={
                        (submitCount > 0 &&
                          !!(errors as any)[
                            employmentFieldNames.designation
                          ]) ||
                        (!!(touched as any)[employmentFieldNames.designation] &&
                          !!(errors as any)[employmentFieldNames.designation])
                      }
                      helperText={
                        (submitCount > 0 ||
                          (touched as any)[employmentFieldNames.designation]) &&
                        (errors as any)[employmentFieldNames.designation]
                          ? String(
                              (errors as any)[employmentFieldNames.designation],
                            )
                          : ""
                      }
                    />
                  </Box>
                </Grid>
              )}
            <Grid item xs={12} sm={6}>
              <Box>
                <label className="text-lable" style={{ display: "block" }}>
                  Monthly Income <span style={{ color: "red" }}>*</span>
                </label>
                <Field name={employmentFieldNames.monthlyIncome}>
                  {({ field: f }: any) => (
                    <InputField
                      {...f}
                      fullWidth
                      placeholder="Enter Monthly Income"
                      variant="outlined"
                      inputProps={{
                        maxLength: 21,
                      }}
                      value={
                        (values as any)[employmentFieldNames.monthlyIncome] ||
                        ""
                      }
                      onChange={(e: any) => {
                        handleAmountFieldChange(
                          employmentFieldNames.monthlyIncome,
                          e.target.value,
                        );
                      }}
                      error={
                        (submitCount > 0 &&
                          !!(errors as any)[
                            employmentFieldNames.monthlyIncome
                          ]) ||
                        (!!(touched as any)[
                          employmentFieldNames.monthlyIncome
                        ] &&
                          !!(errors as any)[employmentFieldNames.monthlyIncome])
                      }
                      helperText={
                        (submitCount > 0 ||
                          (touched as any)[
                            employmentFieldNames.monthlyIncome
                          ]) &&
                        (errors as any)[employmentFieldNames.monthlyIncome]
                          ? String(
                              (errors as any)[
                                employmentFieldNames.monthlyIncome
                              ],
                            )
                          : ""
                      }
                    />
                  )}
                </Field>
              </Box>
            </Grid>
          </Grid>
          )}

          {showFullEmploymentFields && (
              <Box
                sx={{
                  background: "#00b3b514",
                  mt: 2,
                  mb: 1,
                  borderRadius: "8px",
                  padding: "16px",
                }}
              >
                <Box
                  mb={1}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <img src={workExperienceIcon} alt="work-experience" />
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#616161",
                    }}
                  >
                    Help us with your work experience{" "}
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <label
                        className="text-lable"
                        style={{ display: "block" }}
                      >
                        Total Years
                      </label>
                      <Field name={employmentFieldNames.employmentYear}>
                        {({ field: f }: any) => (
                          <InputField
                            {...f}
                            fullWidth
                            placeholder="Enter Total Years"
                            variant="outlined"
                            type="text"
                            inputProps={{
                              maxLength: 2,
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                              autoComplete: "off",
                            }}
                            value={
                              (values as any)[
                                employmentFieldNames.employmentYear
                              ] || ""
                            }
                            onChange={(e: any) =>
                              handleNumericFieldChange(
                                employmentFieldNames.employmentYear,
                                e.target.value,
                                2,
                              )
                            }
                            error={
                              (submitCount > 0 &&
                                !!(errors as any)[
                                  employmentFieldNames.employmentYear
                                ]) ||
                              (!!(touched as any)[
                                employmentFieldNames.employmentYear
                              ] &&
                                !!(errors as any)[
                                  employmentFieldNames.employmentYear
                                ])
                            }
                            helperText={
                              (submitCount > 0 ||
                                (touched as any)[
                                  employmentFieldNames.employmentYear
                                ]) &&
                              (errors as any)[
                                employmentFieldNames.employmentYear
                              ]
                                ? String(
                                    (errors as any)[
                                      employmentFieldNames.employmentYear
                                    ],
                                  )
                                : ""
                            }
                          />
                        )}
                      </Field>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <label
                        className="text-lable"
                        style={{ display: "block" }}
                      >
                        Total Months
                      </label>
                      <Field name={employmentFieldNames.employmentMonth}>
                        {({ field: f }: any) => (
                          <InputField
                            {...f}
                            fullWidth
                            placeholder="Enter Total Months"
                            variant="outlined"
                            type="text"
                            inputProps={{
                              maxLength: 2,
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                              autoComplete: "off",
                            }}
                            value={
                              (values as any)[
                                employmentFieldNames.employmentMonth
                              ] || ""
                            }
                            onChange={(e: any) =>
                              handleNumericFieldChange(
                                employmentFieldNames.employmentMonth,
                                e.target.value,
                                2,
                              )
                            }
                            error={
                              (submitCount > 0 &&
                                !!(errors as any)[
                                  employmentFieldNames.employmentMonth
                                ]) ||
                              (!!(touched as any)[
                                employmentFieldNames.employmentMonth
                              ] &&
                                !!(errors as any)[
                                  employmentFieldNames.employmentMonth
                                ])
                            }
                            helperText={
                              (submitCount > 0 ||
                                (touched as any)[
                                  employmentFieldNames.employmentMonth
                                ]) &&
                              (errors as any)[
                                employmentFieldNames.employmentMonth
                              ]
                                ? String(
                                    (errors as any)[
                                      employmentFieldNames.employmentMonth
                                    ],
                                  )
                                : ""
                            }
                          />
                        )}
                      </Field>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

          {showFullEmploymentFields && (
              <>
                <Box mt={3} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "8px",
                      bgcolor: "#e5feffff",
                    }}
                  >
                    <img src={locationIcon} alt="Company Address Icon" />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#616161",
                      // mt: 3,
                    }}
                  >
                    Company Address
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <label
                        className="text-lable"
                        style={{ display: "block" }}
                      >
                        Address Line 1 <span style={{ color: "red" }}>*</span>
                      </label>
                      <Field name={employmentFieldNames.companyAddress1}>
                        {({ field: f }: any) => (
                          <InputField
                            {...f}
                            fullWidth
                            placeholder="Enter Address Line 1"
                            variant="outlined"
                            value={
                              (values as any)[
                                employmentFieldNames.companyAddress1
                              ] || ""
                            }
                            onChange={(e: any) =>
                              setFieldValue(
                                employmentFieldNames.companyAddress1,
                                e.target.value,
                              )
                            }
                            error={
                              (submitCount > 0 &&
                                !!(errors as any)[
                                  employmentFieldNames.companyAddress1
                                ]) ||
                              (!!(touched as any)[
                                employmentFieldNames.companyAddress1
                              ] &&
                                !!(errors as any)[
                                  employmentFieldNames.companyAddress1
                                ])
                            }
                            helperText={
                              (submitCount > 0 ||
                                (touched as any)[
                                  employmentFieldNames.companyAddress1
                                ]) &&
                              (errors as any)[
                                employmentFieldNames.companyAddress1
                              ]
                                ? String(
                                    (errors as any)[
                                      employmentFieldNames.companyAddress1
                                    ],
                                  )
                                : ""
                            }
                          />
                        )}
                      </Field>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <label
                        className="text-lable"
                        style={{ display: "block" }}
                      >
                        Address Line 2 <span style={{ color: "red" }}>*</span>
                      </label>
                      <Field name={employmentFieldNames.companyAddress2}>
                        {({ field: f }: any) => (
                          <InputField
                            {...f}
                            fullWidth
                            placeholder="Enter Address Line 2"
                            variant="outlined"
                            value={
                              (values as any)[
                                employmentFieldNames.companyAddress2
                              ] || ""
                            }
                            onChange={(e: any) =>
                              setFieldValue(
                                employmentFieldNames.companyAddress2,
                                e.target.value,
                              )
                            }
                            error={
                              (submitCount > 0 &&
                                !!(errors as any)[
                                  employmentFieldNames.companyAddress2
                                ]) ||
                              (!!(touched as any)[
                                employmentFieldNames.companyAddress2
                              ] &&
                                !!(errors as any)[
                                  employmentFieldNames.companyAddress2
                                ])
                            }
                            helperText={
                              (submitCount > 0 ||
                                (touched as any)[
                                  employmentFieldNames.companyAddress2
                                ]) &&
                              (errors as any)[
                                employmentFieldNames.companyAddress2
                              ]
                                ? String(
                                    (errors as any)[
                                      employmentFieldNames.companyAddress2
                                    ],
                                  )
                                : ""
                            }
                          />
                        )}
                      </Field>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <label
                        className="text-lable"
                        style={{ display: "block" }}
                      >
                        Address Line 3 <span style={{ color: "red" }}>*</span>
                      </label>
                      <Field name={employmentFieldNames.companyAddress3}>
                        {({ field: f }: any) => (
                          <InputField
                            {...f}
                            fullWidth
                            placeholder="Enter Address Line 3"
                            variant="outlined"
                            value={
                              (values as any)[
                                employmentFieldNames.companyAddress3
                              ] || ""
                            }
                            onChange={(e: any) =>
                              setFieldValue(
                                employmentFieldNames.companyAddress3,
                                e.target.value,
                              )
                            }
                            error={
                              (submitCount > 0 &&
                                !!(errors as any)[
                                  employmentFieldNames.companyAddress3
                                ]) ||
                              (!!(touched as any)[
                                employmentFieldNames.companyAddress3
                              ] &&
                                !!(errors as any)[
                                  employmentFieldNames.companyAddress3
                                ])
                            }
                            helperText={
                              (submitCount > 0 ||
                                (touched as any)[
                                  employmentFieldNames.companyAddress3
                                ]) &&
                              (errors as any)[
                                employmentFieldNames.companyAddress3
                              ]
                                ? String(
                                    (errors as any)[
                                      employmentFieldNames.companyAddress3
                                    ],
                                  )
                                : ""
                            }
                          />
                        )}
                      </Field>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <label
                        className="text-lable"
                        style={{ display: "block" }}
                      >
                        Landmark <span style={{ color: "red" }}>*</span>
                      </label>
                      <Field name={employmentFieldNames.landmark}>
                        {({ field: f }: any) => (
                          <InputField
                            {...f}
                            fullWidth
                            placeholder="Enter Landmark"
                            variant="outlined"
                            value={
                              (values as any)[employmentFieldNames.landmark] ||
                              ""
                            }
                            onChange={(e: any) =>
                              setFieldValue(
                                employmentFieldNames.landmark,
                                e.target.value,
                              )
                            }
                            error={
                              (submitCount > 0 &&
                                !!(errors as any)[
                                  employmentFieldNames.landmark
                                ]) ||
                              (!!(touched as any)[
                                employmentFieldNames.landmark
                              ] &&
                                !!(errors as any)[
                                  employmentFieldNames.landmark
                                ])
                            }
                            helperText={
                              (submitCount > 0 ||
                                (touched as any)[
                                  employmentFieldNames.landmark
                                ]) &&
                              (errors as any)[employmentFieldNames.landmark]
                                ? String(
                                    (errors as any)[
                                      employmentFieldNames.landmark
                                    ],
                                  )
                                : ""
                            }
                          />
                        )}
                      </Field>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <label
                        className="text-lable"
                        style={{ display: "block" }}
                      >
                        Pincode <span style={{ color: "red" }}>*</span>
                      </label>
                      <InputField
                        fullWidth
                        placeholder="Enter Pincode"
                        type="text"
                        inputProps={{
                          maxLength: 6,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          autoComplete: "off",
                        }}
                        value={
                          (values as any)[employmentFieldNames.pincode] || ""
                        }
                        onChange={(e: any) => {
                          const sanitizedPincode = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6);
                          setFieldValue(
                            employmentFieldNames.pincode,
                            sanitizedPincode,
                          );
                          if (sanitizedPincode.length === 6) {
                            debouncedCompanyZipCodeCall(sanitizedPincode);
                          }
                        }}
                        error={getFieldError(employmentFieldNames.pincode)}
                        helperText={
                          zipCodeLoading
                            ? "Validating pincode..."
                            : getFieldHelperText(employmentFieldNames.pincode)
                        }
                        InputProps={{
                          endAdornment: zipCodeLoading ? (
                            <CircularProgress size={20} />
                          ) : undefined,
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <label
                        className="text-lable"
                        style={{ display: "block" }}
                      >
                        City <span style={{ color: "red" }}>*</span>
                      </label>
                      <Field name={employmentFieldNames.city}>
                        {({ field: f }: any) => (
                          <InputField
                            {...f}
                            fullWidth
                            placeholder="Enter Landmark"
                            variant="outlined"
                            value={
                              (values as any)[employmentFieldNames.city] || ""
                            }
                            onChange={(e: any) =>
                              setFieldValue(
                                employmentFieldNames.city,
                                e.target.value,
                              )
                            }
                            disabled
                            error={
                              (submitCount > 0 &&
                                !!(errors as any)[employmentFieldNames.city]) ||
                              (!!(touched as any)[employmentFieldNames.city] &&
                                !!(errors as any)[employmentFieldNames.city])
                            }
                            helperText={
                              (submitCount > 0 ||
                                (touched as any)[employmentFieldNames.city]) &&
                              (errors as any)[employmentFieldNames.city]
                                ? String(
                                    (errors as any)[employmentFieldNames.city],
                                  )
                                : ""
                            }
                          />
                        )}
                      </Field>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <label
                        className="text-lable"
                        style={{ display: "block" }}
                      >
                        State <span style={{ color: "red" }}>*</span>
                      </label>
                      <Field name={employmentFieldNames.state}>
                        {({ field: f }: any) => (
                          <InputField
                            {...f}
                            fullWidth
                            placeholder="Enter Landmark"
                            variant="outlined"
                            value={
                              (values as any)[employmentFieldNames.state] || ""
                            }
                            onChange={(e: any) =>
                              setFieldValue(
                                employmentFieldNames.state,
                                e.target.value,
                              )
                            }
                            disabled
                            error={
                              (submitCount > 0 &&
                                !!(errors as any)[
                                  employmentFieldNames.state
                                ]) ||
                              (!!(touched as any)[employmentFieldNames.state] &&
                                !!(errors as any)[employmentFieldNames.state])
                            }
                            helperText={
                              (submitCount > 0 ||
                                (touched as any)[employmentFieldNames.state]) &&
                              (errors as any)[employmentFieldNames.state]
                                ? String(
                                    (errors as any)[employmentFieldNames.state],
                                  )
                                : ""
                            }
                          />
                        )}
                      </Field>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <label
                        className="text-lable"
                        style={{ display: "block" }}
                      >
                        Country <span style={{ color: "red" }}>*</span>
                      </label>
                      <Field name={employmentFieldNames.companyCountry}>
                        {({ field: f }: any) => (
                          <InputField
                            {...f}
                            fullWidth
                            placeholder="Enter Landmark"
                            variant="outlined"
                            value={
                              (values as any)[
                                employmentFieldNames.companyCountry
                              ] || ""
                            }
                            disabled
                            onChange={(e: any) =>
                              setFieldValue(
                                employmentFieldNames.companyCountry,
                                e.target.value,
                              )
                            }
                            error={
                              (submitCount > 0 &&
                                !!(errors as any)[
                                  employmentFieldNames.companyCountry
                                ]) ||
                              (!!(touched as any)[
                                employmentFieldNames.companyCountry
                              ] &&
                                !!(errors as any)[
                                  employmentFieldNames.companyCountry
                                ])
                            }
                            helperText={
                              (submitCount > 0 ||
                                (touched as any)[
                                  employmentFieldNames.companyCountry
                                ]) &&
                              (errors as any)[
                                employmentFieldNames.companyCountry
                              ]
                                ? String(
                                    (errors as any)[
                                      employmentFieldNames.companyCountry
                                    ],
                                  )
                                : ""
                            }
                          />
                        )}
                      </Field>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
        </>
      )}
    </Box>
  );
};

export default EmployementDetailsTab;